import type { TFunction } from "i18next";
import { useMemo } from "react";
import U2000SaveCancelDialog from "../../../../../u2000Core/components/dialog/U2000SaveCancelDialog";
import type {
  U2104AvailableLogicalUnitChoice,
  U2104OrganizationAvailableLogicalUnitHandler,
} from "./U2104OrganizationAvailableLogicalUnitHandlers";
import { u2104GroupLogicalUnitsByCategory } from "../../../../tools/u2104GroupLogicalUnitsByCategory";
import { useTranslation } from "react-i18next";

interface U2104OrganizationAvailableLogicalUnitDlgProps {
  handler: U2104OrganizationAvailableLogicalUnitHandler;
  t: TFunction;
  NS: string;
}

function U2104OrganizationAvailableLogicalUnitEditDlg({ handler, t, NS }: U2104OrganizationAvailableLogicalUnitDlgProps) {
  const { i18n } = useTranslation();
  const { formStateHandlers, selectableLogicalUnits, isLoadingChoices } = handler;

  const groupedLogicalUnits = useMemo(
    () =>
      u2104GroupLogicalUnitsByCategory(selectableLogicalUnits, {
        noCategoryLabel: t("lblNoLogicalUnitCategory", { ns: NS }),
        locale: i18n.language,
      }),
    [selectableLogicalUnits, t, NS, i18n.language],
  );

  const areAllToggleableChecked = useMemo(() => {
    const toggleable = selectableLogicalUnits.filter((lu) => !lu.disabled);
    return toggleable.length > 0 && toggleable.every((lu) => lu.idChecked);
  }, [selectableLogicalUnits]);

  const hasToggleable = useMemo(
    () => selectableLogicalUnits.some((lu) => !lu.disabled),
    [selectableLogicalUnits],
  );

  const fieldErrors = formStateHandlers.getErrors();

  return (
    <U2000SaveCancelDialog
      width="x-large"
      height="h-[80vh]"
      isOpen={handler.isDialogOpen}
      onClose={handler.onDlgClose}
      title={t("lblModifyAvailableLogicalUnits", { ns: NS })}
      isValid={true}
    >
      <div className="flex flex-col gap-y-4">
        {fieldErrors?.logicalUnitId && <p className="text-sm text-red-600">{fieldErrors.logicalUnitId}</p>}

        {isLoadingChoices && <p className="text-sm text-gray-600">{t("msgLoadingAvailableLogicalUnits", { ns: NS })}</p>}

        {!isLoadingChoices && selectableLogicalUnits.length > 0 && hasToggleable && (
          <div className="mb-2 flex items-center gap-x-2 border-b pb-2">
            <div
              role="checkbox"
              aria-checked={areAllToggleableChecked}
              tabIndex={0}
              className={`h-6 w-6 cursor-pointer rounded-sm border-2 transition-colors duration-300 ${areAllToggleableChecked ? "border-blue-500 bg-blue-100" : "border-gray-300 bg-white"}`}
              onClick={() => handler.onCheckAllLogicalUnits()}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handler.onCheckAllLogicalUnits();
                }
              }}
            >
              {areAllToggleableChecked && (
                <svg className="m-auto h-5 w-5 text-black" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                  <path d="M5 13l4 4L19 7" />
                </svg>
              )}
            </div>
            <p className="inline-block font-semibold text-gray-800">{t("role.lblSelectAll", { ns: NS })}</p>
          </div>
        )}

        {!isLoadingChoices && selectableLogicalUnits.length === 0 ? (
          <p className="text-sm text-gray-600">{t("msgNoAvailableLogicalUnitCatalog", { ns: NS })}</p>
        ) : (
          !isLoadingChoices && (
            <div className="flex flex-col gap-y-4">
              {groupedLogicalUnits.map((group) => (
                <div key={group.key} className="flex flex-col gap-y-2">
                  <h3 className="border-b border-gray-200 pb-1 text-sm font-semibold text-gray-700">{group.label}</h3>
                  <div className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
                    {group.items.map((lu: U2104AvailableLogicalUnitChoice) => (
                      <div
                        key={lu.id}
                        className={`flex min-w-0 items-center gap-x-2 ${lu.disabled ? "opacity-60" : ""}`}
                        title={lu.disabled ? t("hintAvailableLogicalUnitReferenced", { ns: NS }) : undefined}
                      >
                        <div
                          role="checkbox"
                          aria-checked={lu.idChecked}
                          aria-disabled={lu.disabled}
                          tabIndex={lu.disabled ? -1 : 0}
                          className={`h-6 w-6 shrink-0 rounded border-2 transition-colors duration-300 ${
                            lu.disabled
                              ? "cursor-not-allowed border-gray-300 bg-gray-100"
                              : `cursor-pointer ${lu.idChecked ? "border-blue-500 bg-blue-100" : "border-gray-300 bg-white"}`
                          }`}
                          onClick={() => handler.onCheckLogicalUnit(lu)}
                          onKeyDown={(e) => {
                            if (lu.disabled) return;
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handler.onCheckLogicalUnit(lu);
                            }
                          }}
                        >
                          {lu.idChecked && (
                            <svg className={`m-auto h-5 w-5 ${lu.disabled ? "text-gray-500" : "text-black"}`} fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                              <path d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                        <p className={`min-w-0 truncate ${lu.disabled ? "text-gray-500" : "text-gray-800"}`} title={lu.logicalUnitDescription}>
                          {lu.logicalUnitDescription}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </U2000SaveCancelDialog>
  );
}

export default U2104OrganizationAvailableLogicalUnitEditDlg;
